import { Transform, HttpClient } from "silver";
import { BookControlService } from "core/Services/BookControlService";
import { BookBand } from "core/Controls/Books/BookBand";
import { Markdown } from "shared/Markdown";
import { htmlToNode } from "shared/functions";
import { MyonApiResponse, isMyonApiSuccess } from "shared/Api/MyonApiResponse";
import { MyonLanguageLoader } from "Lang";
import { ContextualPopup } from "shared/Components/ContextualPopup";

type SimpleBookBandConfiguration = {
	bandType: number;
	bandTitle: string;
	books: number[];
	isImage?: boolean;
};

type DynamicBookBandConfiguration = SimpleBookBandConfiguration & {
	dynamicEndpoint: string;
	options: { [ value: string ]: string };
	selectedOption: string;
};

type TooltipBandConfiguration = SimpleBookBandConfiguration & {
	tooltipTitle: string;
	tooltipText: string;
};

export type BookBandConfiguration = SimpleBookBandConfiguration | DynamicBookBandConfiguration | TooltipBandConfiguration;

function configIsDynamic(config: BookBandConfiguration): config is DynamicBookBandConfiguration {
	return config.hasOwnProperty("dynamicEndpoint");
}

function configHasTooltip(config: BookBandConfiguration): config is TooltipBandConfiguration {
	return config.hasOwnProperty("tooltipText");
}

export class BookBandService {
	constructor(
		private bookControlService: BookControlService,
		private lang: MyonLanguageLoader,
		private markdown: Markdown,
		private transform: Transform,
		private httpClient: HttpClient
	) {	}

	public makeFromConfig(config: BookBandConfiguration): BookBand {
		const bookBand = new BookBand(
			config.bandTitle,
			config.books,
			config.bandType,
			this.transform,
			this.bookControlService,
			this
		);
		if (configIsDynamic(config)) {
			bookBand.setDynamicBand(
				config.dynamicEndpoint,
				config.options,
				config.selectedOption
			);
		}
		if (configHasTooltip(config)) {
			bookBand.setContextualPopup(ContextualPopup.makeFromTitleAndText(
				config.tooltipTitle,
				htmlToNode(this.markdown.toHTML(config.tooltipText)),
				"-clear",
				"-controlBandNotice"
			));
		}
		this.lang.load("Pages\\Library\\Recommended").then((bookLang) => {
			bookBand.setLang(bookLang);
		});
		return bookBand;
	}

	public async getBandBookIds(endpoint: string, id: string): Promise<number[]> {
		const response: MyonApiResponse<number[]> = await this.httpClient.post(endpoint, {
			body: { category_id: id }
		});
		if (!isMyonApiSuccess(response)) {
			throw new Error("Invalid response");
		}
		return response.result;
	}
}
